import React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import Avatar from 'components/Avatar'
import Button from 'components/Button'
import {
  Dropdown,
  DropdownDivider,
  DropdownItem,
  DropdownTitle,
} from 'components/Dropdown'
import Icon from 'components/Icon'
import { IconButton } from 'components/IconButton'
import { Membership } from 'iam/WorkspaceMembershipStore'
import { formatToLongDateWithComma } from 'utils/date-util'
import {
  MembershipsTabViewModel,
  MembershipTabs,
} from './MembershipsTabViewModel'
import styles from './MembershipsTab.sass'
import { Id } from 'types/definitions'
import { TabBar, TabItem } from '../../../components/TabBar'
import SearchField from 'components/SearchField'
import { match } from 'utils/pattern-match-util'
import UserManagementEmptyState from 'user-management/components/UserManagementEmptyState'

interface MembershipsTabProps {
  vm: MembershipsTabViewModel
}

export default observer(({ vm }: MembershipsTabProps) => {
  return (
    <>
      <div className={styles.membershipsCountContainer}>
        <span className={styles.membershipsLabel}>Memberships</span>
        <span className={styles.membershipsCount}>({vm.membershipsCount})</span>
      </div>

      <div className={styles.root}>
        <div className={styles.header}>
          <SearchField
            stretch
            autoFocus
            showClearButton
            className={styles.searchBar}
            placeholder="Search"
            value={vm.searchText.value}
            onChange={vm.searchText.change}
            dataTest="workspace-memberships-tab-search-field"
          />
          <div className={styles.headerActions}>
            <IconButton
              onClick={() => {
                if (vm.canJoinWorkspaceAsAdmin) {
                  vm.showJoinWorkspaceAsAdminDialog()
                } else if (vm.isCustomerUser) {
                  vm.showAddCustomerToWorkspaceDialog()
                } else {
                  vm.showAddUserToWorkspaceDialog()
                }
              }}
              dataTest={
                vm.canJoinWorkspaceAsAdmin
                  ? 'memberships-join-workspace-as-admin'
                  : vm.isCustomerUser
                    ? 'memberships-add-customer-to-workspace'
                    : 'memberships-add-user-to-workspace'
              }
            >
              <Icon name="add" size={24} color="var(--grey600)" />
            </IconButton>
          </div>
        </div>

        <div className={styles.membershipTabs}>
          <TabBar>
            <TabItem
              active={vm.tabController.current.tab === MembershipTabs.Active}
              onClick={() => vm.setCurrentMembershipTab(MembershipTabs.Active)}
              dataTest="workspace-memberships-tab-active"
            >
              Active
            </TabItem>
            <TabItem
              active={vm.tabController.current.tab === MembershipTabs.Inactive}
              onClick={() =>
                vm.setCurrentMembershipTab(MembershipTabs.Inactive)
              }
              dataTest="workspace-memberships-tab-inactive"
            >
              Inactive
            </TabItem>
          </TabBar>
        </div>

        <div className={styles.membershipsList}>
          {match(vm.tabController.current.tab, {
            [MembershipTabs.Active]: () =>
              vm.activeMemberships.length ? (
                vm.activeMemberships.map((membership) => (
                  <MembershipRow
                    vm={vm}
                    membership={membership}
                    key={membership.workspace.id}
                  />
                ))
              ) : (
                <UserManagementEmptyState
                  isSearchFilterApplied={!vm.isSearchTextEmpty}
                  onClearSearchFilterClick={() => vm.clearFilters()}
                />
              ),
            [MembershipTabs.Inactive]: () =>
              vm.inactiveMemberships.length ? (
                vm.inactiveMemberships.map((membership) => (
                  <MembershipRow
                    vm={vm}
                    membership={membership}
                    key={membership.workspace.id}
                  />
                ))
              ) : (
                <UserManagementEmptyState
                  isSearchFilterApplied={!vm.isSearchTextEmpty}
                  onClearSearchFilterClick={() => vm.clearFilters()}
                />
              ),
          })}
        </div>
      </div>
    </>
  )
})

interface MembershipRowProps {
  vm: MembershipsTabViewModel
  membership: Membership
}

const MembershipRow = observer(({ vm, membership }: MembershipRowProps) => {
  const active = vm.selectedMembership === membership
  const workspaceAccess = vm.workspaceAccess[Id.assert(membership.workspace.id)]
  const useMagicLink =
    vm.isCustomerUser &&
    membership.workspace.config?.external_customers_config?.enabled &&
    vm.currentMember.user.platformAdmin

  return (
    <div className={cx(styles.membershipRow, active && styles.active)}>
      <div className={styles.membershipInfo}>
        <div
          className={
            membership.member.inactive
              ? styles.disabled
              : styles.membershipInfoIco
          }
        >
          <Avatar
            size={32}
            subject={{ avatar: membership.workspace.branding.logos.icon }}
          />
        </div>
        <div className={styles.membershipInfoText}>
          <span className={styles.membershipInfoTextTitle}>
            {membership.workspace.name}
          </span>
          <span className={styles.membershipInfoTextSubtitle}>
            {membership.member.roles.find((r) => r.name)?.name}
            <span className={styles.membershipInfoTextSubtitleDivider}>|</span>
            {formatToLongDateWithComma(membership.member.dateCreated)}
          </span>
        </div>
      </div>
      {workspaceAccess && (
        <div className={styles.membershipActions}>
          {!membership.member.inactive && (
            <Button
              className={styles.membershipViewButton}
              type="text"
              onClick={() => vm.openSidebar(membership)}
              dataTest={`memberships-row-open-sidebar-${membership.workspace.id}`}
            >
              View
            </Button>
          )}

          <Dropdown
            autoDismiss
            position="bottom-end"
            button={
              <IconButton
                dataTest={`memberships-row-actions-${membership.workspace.id}`}
              >
                <Icon
                  name="more"
                  size={24}
                  className={styles.membershipActionsButton}
                />
              </IconButton>
            }
          >
            <DropdownTitle>Actions</DropdownTitle>
            {membership.member.inactive ? (
              <>
                {workspaceAccess.canManageWorkspaceMemberships && (
                  <DropdownItem
                    onClick={() =>
                      vm.restoreWorkspaceMembership(
                        vm.user,
                        membership.workspace,
                      )
                    }
                    dataTest={`memberships-row-actions-restore-to-workspace-${membership.workspace.id}`}
                  >
                    Restore membership
                  </DropdownItem>
                )}
              </>
            ) : (
              <>
                {workspaceAccess.canAddUserToTeam && (
                  <DropdownItem
                    onClick={() =>
                      vm.showAddUserToTeamDialogForWorkspace(
                        membership.workspace,
                      )
                    }
                    dataTest="memberships-row-actions-add-to-team"
                  >
                    Add user to team
                  </DropdownItem>
                )}
                {workspaceAccess.canEditUserRole && (
                  <DropdownItem
                    onClick={() =>
                      vm.editUserRole(vm.user, membership.workspace)
                    }
                    dataTest={`memberships-row-actions-edit-role-${membership.workspace.id}`}
                  >
                    Edit role
                  </DropdownItem>
                )}
                {workspaceAccess.canEditUserSkills && (
                  <DropdownItem
                    onClick={() => vm.editSkills(vm.user, membership.workspace)}
                    dataTest={`memberships-row-actions-edit-skills-${membership.workspace.id}`}
                  >
                    Edit skills
                  </DropdownItem>
                )}
                {workspaceAccess.canManageUserCapacity && (
                  <DropdownItem
                    onClick={() =>
                      vm.manageCapacity(vm.user, membership.workspace)
                    }
                    dataTest={`memberships-row-actions-manage-capacity-${membership.workspace.id}`}
                  >
                    Manage capacity
                  </DropdownItem>
                )}
                {workspaceAccess.canManageWorkspaceMemberships && (
                  <DropdownItem
                    onClick={() =>
                      vm.removeFromWorkspace(vm.user, membership.workspace)
                    }
                    dataTest={`memberships-row-actions-remove-from-workspace-${membership.workspace.id}`}
                  >
                    Remove from workspace
                  </DropdownItem>
                )}
                {(workspaceAccess.canImpersonateAsClient ||
                  workspaceAccess.canImpersonateAsPro) &&
                  !useMagicLink && (
                    <>
                      <DropdownDivider />
                      <DropdownTitle>Impersonation</DropdownTitle>

                      {workspaceAccess.canImpersonateAsClient && (
                        <DropdownItem
                          onClick={() =>
                            vm.impersonate(membership.workspace, 'client')
                          }
                          dataTest={`memberships-row-actions-impersonate-as-client-${membership.workspace.id}`}
                        >
                          Impersonate on Customer Portal
                        </DropdownItem>
                      )}

                      {workspaceAccess.canImpersonateAsPro && (
                        <DropdownItem
                          onClick={() =>
                            vm.impersonate(membership.workspace, 'provider')
                          }
                          dataTest={`memberships-row-actions-impersonate-as-provider-${membership.workspace.id}`}
                        >
                          Impersonate on Work Portal
                        </DropdownItem>
                      )}
                    </>
                  )}

                {useMagicLink && (
                  <>
                    <DropdownDivider />
                    <DropdownTitle>Magic Link</DropdownTitle>

                    {workspaceAccess.canImpersonateAsClient && (
                      <DropdownItem
                        disabled={vm.generateMagicLink.pending}
                        onClick={() =>
                          vm.generateMagicLink(
                            membership.member.user.publicId,
                            membership.workspace.id,
                          )
                        }
                      >
                        Generate Magic Link
                      </DropdownItem>
                    )}
                  </>
                )}
              </>
            )}
          </Dropdown>
        </div>
      )}
    </div>
  )
})
